import React, { useState, useEffect } from 'react';
import { Sparkles, RefreshCw, Sun, Lightbulb, Star } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { API } from '@/utils/api';

// Fallback prompts when API fails
const FALLBACK_PROMPTS = [
  "What are three things you're grateful for this morning?",
  "Describe a moment from yesterday that made you smile.",
  "What intention do you want to set for today?",
  "What's something you're looking forward to this week?",
  "Write about a challenge you overcame recently.",
];

// Daily prompt component - shows near entry creation
// Fetches a NEW random prompt each time it mounts (each "New Entry" click)
export const DailyPrompt = ({ onUsePrompt }) => {
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const { isGradientMode } = useTheme();

  useEffect(() => {
    // Fetch a random prompt each time this component mounts
    fetchPrompt();
  }, []);

  const fetchPrompt = async () => {
    setLoading(true);
    try {
      // Use random=true to get a different prompt each time
      const response = await fetch(`${API}/prompts/daily?random=true`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setPrompt(data);
      } else {
        // Use fallback prompt
        const fallback = FALLBACK_PROMPTS[Math.floor(Math.random() * FALLBACK_PROMPTS.length)];
        setPrompt({ prompt: fallback });
      }
    } catch (error) {
      console.error('Failed to fetch prompt:', error);
      // Use fallback prompt
      const fallback = FALLBACK_PROMPTS[Math.floor(Math.random() * FALLBACK_PROMPTS.length)];
      setPrompt({ prompt: fallback });
    } finally {
      setLoading(false);
    }
  };

  // Function to get a new prompt
  const refreshPrompt = () => {
    fetchPrompt();
  };

  if (!isVisible || loading) return null;
  if (!prompt?.prompt) return null;

  return (
    <div 
      className="rounded-xl p-4 mb-4"
      style={{
        background: isGradientMode 
          ? `linear-gradient(to right, var(--theme-background-secondary), rgba(var(--theme-primary-rgb), 0.2))`
          : 'var(--theme-background-secondary)',
        border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.3)'
      }}
    >
      <div className="flex items-start gap-3">
        <div 
          className="p-2 rounded-lg shrink-0"
          style={{ backgroundColor: 'var(--theme-background)' }}
        >
          <Lightbulb className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="text-xs mb-1" style={{ color: 'var(--theme-text-secondary)' }}>Writing Prompt</p>
            <button
              onClick={refreshPrompt}
              className="p-1 hover:bg-[var(--theme-background-secondary)] rounded transition-colors"
              title="Get another prompt"
            >
              <RefreshCw className="w-3.5 h-3.5" style={{ color: 'var(--theme-text-secondary)' }} />
            </button>
          </div>
          <p className="font-medium" style={{ color: 'var(--theme-text)' }}>{prompt?.prompt}</p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => onUsePrompt && onUsePrompt(prompt?.prompt)}
              className="px-3 py-1.5 text-white text-xs font-medium rounded-lg hover:opacity-90 transition-opacity"
              style={{ background: 'var(--theme-button-bg)' }}
              data-testid="use-prompt-btn"
            >
              Use This Prompt
            </button>
            <button
              onClick={() => setIsVisible(false)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg hover:opacity-80 transition-colors"
              style={{ backgroundColor: 'var(--theme-background-secondary)', color: 'var(--theme-text-secondary)' }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Morning affirmation card for dashboard
export const MorningAffirmation = ({ userId }) => {
  const [affirmation, setAffirmation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const { isGradientMode } = useTheme();
  
  // Use "Daily Affirmation" for all users
  const affirmationTitle = "Daily Affirmation";

  useEffect(() => {
    // Fetch affirmation when userId is available
    if (userId) {
      fetchAffirmation();
    }
  }, [userId]);

  const fetchAffirmation = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setError(false);
    
    try {
      const url = refresh 
        ? `${API}/affirmation?refresh=true` 
        : `${API}/affirmation`;
      const response = await fetch(url, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAffirmation(data);
      } else {
        setError(true);
      }
    } catch (error) {
      console.error('Failed to fetch affirmation:', error);
      setError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Show loading skeleton while fetching
  if (loading && !affirmation) {
    return (
      <div 
        className="rounded-2xl p-6 mb-6"
        style={{
          background: isGradientMode 
            ? `linear-gradient(to bottom right, rgba(var(--theme-secondary-rgb), 0.2), rgba(var(--theme-primary-rgb), 0.2), var(--theme-background-secondary))`
            : 'var(--theme-background-secondary)',
          border: '1px solid rgba(var(--theme-secondary-rgb), 0.3)'
        }}
      >
        <div className="flex items-start gap-4">
          <div 
            className="p-3 rounded-xl shrink-0"
            style={{ background: 'var(--theme-button-bg)' }}
          >
            <Sun className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold" style={{ color: 'var(--theme-text)' }}>{affirmationTitle}</h3>
              <Sparkles className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
            </div>
            <div className="flex items-center gap-2">
              <div 
                className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: 'var(--theme-secondary)', borderTopColor: 'transparent' }}
              />
              <p className="text-sm italic" style={{ color: 'var(--theme-text-secondary)' }}>Creating today's affirmation just for you...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If error or no affirmation, show fallback
  if (error || !affirmation) {
    return (
      <div 
        className="rounded-2xl p-6 mb-6"
        style={{
          background: isGradientMode 
            ? `linear-gradient(to bottom right, rgba(var(--theme-secondary-rgb), 0.2), rgba(var(--theme-primary-rgb), 0.2), var(--theme-background-secondary))`
            : 'var(--theme-background-secondary)',
          border: '1px solid rgba(var(--theme-secondary-rgb), 0.3)'
        }}
      >
        <div className="flex items-start gap-4">
          <div 
            className="p-3 rounded-xl shrink-0"
            style={{ background: 'var(--theme-button-bg)' }}
          >
            <Sun className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
                <span>{affirmationTitle}</span>
                <Sparkles className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
              </h3>
              <button
                onClick={() => fetchAffirmation(true)}
                className="p-1.5 rounded-lg hover:bg-[var(--theme-background-secondary)] transition-colors"
                title="Load affirmation"
              >
                <RefreshCw className="w-4 h-4" style={{ color: 'var(--theme-text-secondary)' }} />
              </button>
            </div>
            <p className="text-lg font-medium italic leading-relaxed" style={{ color: 'var(--theme-text)' }}>
              "Today I choose to embrace my unique journey and celebrate every small victory."
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative rounded-2xl p-6 mb-6 overflow-hidden"
      style={{
        background: isGradientMode 
          ? `linear-gradient(to bottom right, rgba(var(--theme-secondary-rgb), 0.2), rgba(var(--theme-primary-rgb), 0.2), var(--theme-background-secondary))`
          : 'var(--theme-background-secondary)',
        border: '1px solid var(--theme-border)'
      }}
    >
      {/* Decorative elements */}
      <Sparkles className="absolute top-3 right-3 w-5 h-5 opacity-60" style={{ color: 'var(--theme-primary)' }} />
      <Star className="absolute bottom-3 left-3 w-4 h-4 opacity-60" style={{ color: 'var(--theme-primary)' }} />
      
      <div className="flex items-start gap-4">
        <div 
          className="p-3 rounded-xl shrink-0"
          style={{ background: 'var(--theme-button-bg)' }}
        >
          <Sun className="w-6 h-6 text-white" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
              <span>{affirmationTitle}</span>
              <Sparkles className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
            </h3>
            <button
              onClick={() => fetchAffirmation(true)}
              disabled={refreshing}
              className="p-1.5 rounded-lg hover:bg-[var(--theme-background-secondary)] transition-colors disabled:opacity-50"
              title="Refresh affirmation"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} style={{ color: 'var(--theme-text-secondary)' }} />
            </button>
          </div>
          
          <p className="text-lg font-medium italic leading-relaxed" style={{ color: 'var(--theme-text)' }}>
            "{affirmation?.affirmation}"
          </p>
          
          {affirmation?.based_on_moods?.length > 0 && (
            <p className="text-xs mt-3" style={{ color: 'var(--theme-text-secondary)', opacity: 0.6 }}>
              ✨ Personalized based on your recent moods
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyPrompt;
